import { useReducer } from "react";

import { addMinutes, differenceInMinutes, getHours, getMinutes, set, subMinutes } from "date-fns";

export type TimeState = {
	start: Date;
	end: Date;
	isAllDay: boolean;

	timezone?: string;
};

export type DateTimeAction =
	| {
			type: "TOGGLE_ALL_DAY" | "CHANGE_START" | "CHANGE_END" | "SHIFT";
			newTime?: Date;
	  }
	| {
			type: "SET";
			newStart: Date;
			newEnd: Date;
			isAllDay: boolean;
	  };

function timeStateReducer(prevState: TimeState, params: DateTimeAction) {
	if (params.type === "SET") {
		const endDate = params.newEnd > params.newStart ? params.newEnd : addMinutes(params.newStart, 30);
		return {
			start: new Date(params.newStart),
			end: new Date(endDate),
			isAllDay: params.isAllDay,
		};
	}

	const { type, newTime } = params;

	if (type === "TOGGLE_ALL_DAY") {
		const newAllDay = !prevState.isAllDay;
		return {
			...prevState,
			isAllDay: newAllDay,

			// When toggling the "All day" checkbox, without this, it's possible to set the end time to a different day than
			// 	the start time when the checkbox is off
			// When the checkbox is off the times appear to be on the same day. This ensures that they actually are
			end: newAllDay
				? prevState.end // Turned on: keep the end time the same
				: // Turned off: set the end time to the same day as the start time
					set(prevState.start, {
						hours: getHours(prevState.end),
						minutes: getMinutes(prevState.end),
						seconds: 0,
						milliseconds: 0,
					}),
		};
	}

	if (!newTime) return prevState;

	switch (type) {
		case "CHANGE_START": {
			if (differenceInMinutes(newTime, prevState.end) >= 0) {
				const eventDuration = differenceInMinutes(prevState.end, prevState.start);

				return {
					...prevState,
					start: new Date(newTime),
					// Prevents the end from being before the start
					end: addMinutes(newTime, eventDuration),
				};
			}

			return {
				...prevState,
				start: new Date(newTime),
			};
		}
		case "CHANGE_END": {
			const newEventDuration = differenceInMinutes(newTime, prevState.start);

			if (newEventDuration === 0) {
				// A diff of 0 is usually achievable with the resize handles.
				// I don't think we can stop the handles, so this is the next best thing.
				return {
					...prevState,
					end: addMinutes(newTime, 15),
				};
			}

			if (newEventDuration < 0) {
				return {
					...prevState,
					end: new Date(newTime),
					// Prevents the end from being before the start
					start: subMinutes(newTime, 15),
				};
			}

			return {
				...prevState,
				end: new Date(newTime),
			};
		}
		case "SHIFT": {
			const eventDuration = differenceInMinutes(prevState.end, prevState.start);

			return {
				...prevState,
				start: new Date(newTime),
				end: addMinutes(newTime, eventDuration),
			};
		}
		default: {
			return prevState;
		}
	}
}

/**
 * A reducer for managing the start and end times of an event.
 * @param initial A method for setting the initial state
 * @returns timeState — Guaranteed to be a valid set of start and end times for an event
 * @returns dispatch — A function for safely changing the time
 */
export default function useDateTimeState(initial: () => TimeState) {
	return useReducer(timeStateReducer, null, initial);
}
